import { DatePickerInput, DateSelection, Flex, FormControl, InputV2Props, OnDatePickerChange, TextV2, TimePicker } from '@withjoy/joykit';
import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { getFieldMetadata } from '@shared/components/ScheduleEditor/hooks/getFieldMetadata';
import { FieldMetadata } from '@shared/components/ScheduleEditor/hooks/useScheduleFormState';
import type { ScheduleEditorData } from '@shared/components/ScheduleEditor/ScheduleEditor.types';
import { combineDateAndTime } from '../combineDateAndTime';

type FormDatePickerProps = {
  fieldName: 'startTime' | 'endTime';
  state: ScheduleEditorData;
  setValue: (fieldName: FormDatePickerProps['fieldName'], value: DateSelection) => void;
  label: string;
  meta: Record<keyof ScheduleEditorData, FieldMetadata>;
  minTime?: string;
} & Omit<InputV2Props, 'value' | 'onChange'>;

function format24Hour(date: DateSelection) {
  // fallback to 00:00 if date is not set
  if (!date) {
    return '00:00';
  }

  return format(date, 'HH:mm');
}

export const FormDatePicker = (props: FormDatePickerProps) => {
  const { state, setValue, fieldName, meta, label, minTime } = props;
  const metadata = getFieldMetadata(fieldName, meta);

  const { date, time } = useMemo(
    () => ({
      date: state[fieldName],
      time: format24Hour(state[fieldName]),
    }),
    [state, fieldName],
  );

  const handleDateChange: OnDatePickerChange = newDate => {
    if (newDate instanceof Date) {
      const finalDate = combineDateAndTime(newDate, time);
      setValue(fieldName, finalDate);
    }
  };

  const handleTimeChange = (newTime: string | undefined) => {
    // fallback to 00:00 if time is not set
    const finalDate = combineDateAndTime(date, newTime || '00:00');
    setValue(fieldName, finalDate);
  };

  return (
    <FormControl
      label={<TextV2 typographyVariant="label3">{label}</TextV2>}
      isInvalid={metadata.error}
    >
      <Flex gap="16px">
        <DatePickerInput
          placement="bottom"
          appendPickerToBody={true}
          value={date}
          onChange={handleDateChange}
        />
        <TimePicker
          name={`timePicker-${fieldName}`}
          value={time}
          onChange={handleTimeChange}
          clearable={false}
          min={minTime}
        />
      </Flex>
    </FormControl>
  );
};
